// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { notificationApi } from '../api/notificationApi';
import type { AdvisorNotification, NotificationState } from '../types/notification';
import { isNotificationRead } from '../types/notification';

interface NotificationContextType extends NotificationState {
  fetchNotifications: (reset?: boolean) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

const POLL_INTERVAL = 30000; // 30 seconds fallback polling

type NotificationProviderProps = Readonly<{
  children: ReactNode;
}>;

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    hasMore: true,
    page: 0,
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (reset = false) => {
    const page = reset ? 0 : state.page;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await notificationApi.getNotifications({ page, size: 20 });
      const unreadCount = await notificationApi.getUnreadCount();

      setState((prev) => ({
        ...prev,
        notifications: reset
          ? response.content
          : [...prev.notifications, ...response.content],
        unreadCount,
        page: response.page + 1,
        hasMore: response.page < response.totalPages - 1,
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load notifications',
      }));
    }
  }, [state.page]);

  // Refresh unread count only
  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await notificationApi.getUnreadCount();
      setState((prev) => ({ ...prev, unreadCount: count }));
    } catch (error) {
      console.error('Failed to refresh unread count:', error);
    }
  }, []);

  // Mark single notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setState((prev) => ({
        ...prev,
        notifications: prev.notifications.map((n) =>
          n.id === id ? { ...n, status: 'READ', isRead: true, readAt: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, prev.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      setState((prev) => ({
        ...prev,
        notifications: prev.notifications.map((n) => ({
          ...n,
          status: 'READ' as const,
          isRead: true,
          readAt: new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, []);

  // Delete a notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      await notificationApi.deleteNotification(id);
      setState((prev) => {
        const deleted = prev.notifications.find((n) => n.id === id);
        return {
          ...prev,
          notifications: prev.notifications.filter((n) => n.id !== id),
          unreadCount: deleted && !deleted.isRead
            ? Math.max(0, prev.unreadCount - 1)
            : prev.unreadCount,
        };
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, []);

  // Handle incoming SSE notification
  const handleNewNotification = useCallback((notification: AdvisorNotification) => {
    setState((prev) => ({
      ...prev,
      notifications: [notification, ...prev.notifications],
      unreadCount: prev.unreadCount + 1,
    }));
  }, []);

  // Setup SSE connection
  const setupEventSource = useCallback(async () => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // createEventSource is now async - it first fetches the SSE token from BFF
      const eventSource = await notificationApi.createEventSource();
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('notification', (event) => {
        try {
          const raw = JSON.parse(event.data);
          const notification: AdvisorNotification = {
            ...raw,
            type: raw.notificationType ?? raw.type,
            status: raw.status ?? 'PENDING',
            isRead: isNotificationRead(raw.status ?? 'PENDING'),
          };
          handleNewNotification(notification);
        } catch (error) {
          console.error('Failed to parse notification event:', error);
        }
      });

      eventSource.addEventListener('connected', () => {
        console.log('SSE connected for notifications');
      });

      eventSource.addEventListener('heartbeat', () => {
        // Connection is alive
      });

      eventSource.onerror = () => {
        console.warn('SSE connection error, will retry...');
        eventSource.close();
        eventSourceRef.current = null;
        // Retry connection after delay
        setTimeout(() => {
          setupEventSource().catch(console.error);
        }, 5000);
      };
    } catch (error) {
      console.error('Failed to setup SSE:', error);
      // Retry after delay on failure
      setTimeout(() => {
        setupEventSource().catch(console.error);
      }, 5000);
    }
  }, [handleNewNotification]);

  // Setup polling as fallback
  const setupPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = globalThis.setInterval(() => {
      refreshUnreadCount();
    }, POLL_INTERVAL);
  }, [refreshUnreadCount]);

  // Initialize on mount - intentionally run only once, these functions are stable for initial setup
  useEffect(() => {
    // Fetch initial notifications
    fetchNotifications(true);

    // Setup SSE for real-time updates (async)
    setupEventSource().catch(console.error);

    // Setup polling as fallback
    setupPolling();

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<NotificationContextType>(() => ({
    ...state,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshUnreadCount,
  }), [state, fetchNotifications, markAsRead, markAllAsRead, deleteNotification, refreshUnreadCount]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
